/**
 * FlipZoneDetector.js
 * ─────────────────────────────────────────────────────────────
 * Detects "flip zones" via two birth paths:
 *
 * 1. SPIKE — A single dominant candle's POC (point of control)
 *    becomes a zone. Catches sudden volume events.
 *
 * 2. CLUSTER — Rolling volume profile across N candles. The
 *    HVN (high volume node) — the mcap level with the
 *    most accumulated volume — becomes a zone. Catches
 *    sustained battles where no single candle stands out.
 *
 * Zone lifecycle:
 *   BIRTH → CONFIRM (reversal touches) → DECAY (volume dries up)
 *
 * Works directly with SolCheddar candle format.
 * ─────────────────────────────────────────────────────────────
 */

// Canonical per-level iterator — abstracts over historical / _native / _display
// / _liveLevels and yields REAL mcap with buy/sell split. Live candles store
// their footprint in `_liveLevels` (a Map keyed by bucket INDEX), NOT the
// legacy `mcapLevels` object this file used to read — so every live-candle
// read here returned empty and no zones were ever born from live data. Using
// forEachLevel is the same path the renderer uses, so detector and chart agree.
import { forEachLevel } from '../utils/candleHelpers'

export class FlipZoneDetector {
  /**
   * @param {Object} config
   * @param {number} config.volumeMultiplier    - Candle vol >= localAvg * this = "dominant" (default 1.3)
   * @param {number} config.volWindow           - Rolling window for local avg + profile (default 20)
   * @param {number} config.hvnMultiplier       - Profile level vol >= avgLevelVol * this = HVN (default 1.8)
   * @param {number} config.profileScanEvery    - Scan profile for HVNs every N candles (default 3)
   * @param {number} config.touchRadiusPercent  - FLOOR % for the zone radius (default 0.2%).
   *                                              Active radius = max(price×this, ATR×atrMult):
   *                                              quiet charts behave exactly as the old fixed-%
   *                                              detector; radii expand with volatility instead
   *                                              of shrinking (relatively) on wide-range moves.
   * @param {number} config.atrMult             - Zone radius in ATRs once ATR dominates (default 0.35)
   * @param {number} config.clusterRadiusMult   - Merge zones within this × radius (default 2.5 —
   *                                              preserves the old 0.5%/0.2% cluster:touch ratio
   *                                              at the floor; clusterPercent config removed)
   * @param {number} config.hvnProximityAtrMult - HVN birth window floor in ATRs (default 3)
   * @param {number} config.decayRatio          - Retest vol < localAvg * this = decaying (default 0.4)
   * @param {number} config.maxZones            - Max tracked zones (default 10)
   * @param {number} config.minTouches          - Min touches before zone renders (default 2)
   * @param {number} config.cooldownCandles     - Candles price must be outside before re-touch (default 3)
   * @param {number} config.maxAgeBars          - Remove zones not touched in this many candles (default 500)
   * @param {number} config.hvnProximityPercent - Only birth HVN within this % of current price (default 3%)
   */
  constructor(config = {}) {
    this.volumeMultiplier    = config.volumeMultiplier    ?? 1.3
    this.volWindow           = config.volWindow           ?? 20
    this.hvnMultiplier       = config.hvnMultiplier       ?? 1.8
    this.profileScanEvery    = config.profileScanEvery    ?? 3
    this.touchRadiusPercent  = config.touchRadiusPercent  ?? 0.002
    this.atrMult             = config.atrMult             ?? 0.35
    this.clusterRadiusMult   = config.clusterRadiusMult   ?? 2.5
    this.hvnProximityAtrMult = config.hvnProximityAtrMult ?? 3
    this.decayRatio          = config.decayRatio          ?? 0.4
    this.maxZones            = config.maxZones            ?? 10
    this.minTouches          = config.minTouches          ?? 2
    this.cooldownCandles     = config.cooldownCandles     ?? 3
    this.maxAgeBars          = config.maxAgeBars          ?? 500
    this.hvnProximityPercent = config.hvnProximityPercent ?? 0.03

    this.zones        = []
    this.avgVolume    = 0
    this._volRing     = []
    this._candleRing  = []
    this._profile     = {}
    this._ingestCount = 0
    this._barIndex    = 0  // monotonic candle counter
    this._lastClose   = 0  // track current price for HVN proximity
    this._trRing      = [] // true-range ring (volWindow) — feeds ATR
    this.atr          = 0  // rolling ATR: the ONE adaptive length scale for zone geometry

    // FIX: previous candle state for approach direction detection
    this._prevHigh  = 0
    this._prevLow   = 0
    this._prevClose = 0
  }

  /**
   * Bulk-process raw SolCheddar candles.
   * Call in useMemo keyed to chartData.candles.
   */
  processCandles(rawCandles) {
    this.reset()
    for (const candle of rawCandles) {
      this._ingest(candle)
    }
    return this.getZones()
  }

  _ingest(candle) {
    const vol = this._getCandleVolume(candle)
    if (vol <= 0) return

    this._barIndex++

    // ── Rolling avg volume ──
    this._volRing.push(vol)
    if (this._volRing.length > this.volWindow) this._volRing.shift()
    let sum = 0
    for (let i = 0; i < this._volRing.length; i++) sum += this._volRing[i]
    this.avgVolume = sum / this._volRing.length

    // ── Rolling profile ──
    this._addToProfile(candle)
    this._candleRing.push(candle)
    if (this._candleRing.length > this.volWindow) {
      this._removeFromProfile(this._candleRing.shift())
    }

    const high  = this._getHigh(candle)
    const low   = this._getLow(candle)
    const close = this._getClose(candle)
    const time  = candle.startTime ?? 0
    this._lastClose = close

    // ── Rolling ATR (same window as the volume avg) ──
    // _prevClose is still LAST bar's close here (overwritten at the bottom
    // of _ingest), so TR is the true max(h−l, |h−pc|, |l−pc|).
    const tr = this._prevClose > 0
      ? Math.max(high - low, Math.abs(high - this._prevClose), Math.abs(low - this._prevClose))
      : (high - low)
    if (Number.isFinite(tr) && tr > 0) {
      this._trRing.push(tr)
      if (this._trRing.length > this.volWindow) this._trRing.shift()
      let trSum = 0
      for (let i = 0; i < this._trRing.length; i++) trSum += this._trRing[i]
      this.atr = trSum / this._trRing.length
    }

    // Re-derive every zone's band from the CURRENT radius before touch
    // detection, so bands breathe with ATR instead of freezing at whatever
    // the radius was at birth. Together with the renderer drawing
    // upper/lower directly, this keeps drawn band ≡ touch band always.
    this._refreshZoneBands()

    // ── Touch check — reversal only, with cooldown ──
    // FIX: pass previous candle values so approach direction uses bar[1] data
    this._checkTouches(high, low, close, vol, time, this._prevHigh, this._prevLow, this._prevClose)

    // ── Birth path 1: Single candle POC spike ──
    if (vol >= this.avgVolume * this.volumeMultiplier) {
      const poc = this._getCandlePOC(candle)
      if (poc != null) {
        this._birthOrMerge(poc, vol, time, 'poc')
      }
    }

    // ── Birth path 2: Rolling profile HVN scan ──
    this._ingestCount++
    if (this._ingestCount % this.profileScanEvery === 0) {
      this._scanProfileForHVN(time)
    }

    // FIX: removed unused `time` argument from _pruneZones call
    this._pruneZones()

    // FIX: store this candle for next bar's approach direction check
    this._prevHigh  = high
    this._prevLow   = low
    this._prevClose = close
  }

  // ─── Volume Profile (rolling) ───────────────────────────────

  _addToProfile(candle) {
    if (!candle.isHistorical) {
      forEachLevel(candle, (mcap, buy, sell) => {
        const v = (buy || 0) + (sell || 0)
        if (v > 0) {
          const key = Math.round(mcap).toString()
          this._profile[key] = (this._profile[key] || 0) + v
        }
      })
    } else if (
      candle.isHistorical && candle.volume_tokens > 0 &&
      candle.high_mcap != null && candle.low_mcap != null
    ) {
      const hi    = candle.high_mcap
      const lo    = candle.low_mcap
      const range = hi - lo
      if (range <= 0) return
      // FIX: fixed 10-bucket distribution ensures volPer * iterations === volume_tokens exactly,
      // eliminating the systematic volume inflation from the old Math.round bucket count mismatch
      const buckets    = 10
      const bucketSize = range / buckets
      const volPer     = candle.volume_tokens / buckets
      for (let b = 0; b < buckets; b++) {
        const key = Math.round(lo + (b + 0.5) * bucketSize).toString()
        this._profile[key] = (this._profile[key] || 0) + volPer
      }
    }
  }

  _removeFromProfile(candle) {
    if (!candle.isHistorical) {
      // Exact mirror of _addToProfile — same rounded-mcap keys, same values —
      // so add/remove always cancel as the rolling window slides.
      forEachLevel(candle, (mcap, buy, sell) => {
        const v = (buy || 0) + (sell || 0)
        if (v > 0) {
          const key = Math.round(mcap).toString()
          this._profile[key] = (this._profile[key] || 0) - v
          if (this._profile[key] <= 0) delete this._profile[key]
        }
      })
    } else if (
      candle.isHistorical && candle.volume_tokens > 0 &&
      candle.high_mcap != null && candle.low_mcap != null
    ) {
      const hi    = candle.high_mcap
      const lo    = candle.low_mcap
      const range = hi - lo
      if (range <= 0) return
      // FIX: exactly mirrors _addToProfile — same keys, same volPer — so add/remove always cancel
      const buckets    = 10
      const bucketSize = range / buckets
      const volPer     = candle.volume_tokens / buckets
      for (let b = 0; b < buckets; b++) {
        const key = Math.round(lo + (b + 0.5) * bucketSize).toString()
        this._profile[key] = (this._profile[key] || 0) - volPer
        if (this._profile[key] <= 0) delete this._profile[key]
      }
    }
  }

  _scanProfileForHVN(time) {
    const keys = Object.keys(this._profile)
    if (keys.length < 3) return

    let totalLevelVol = 0
    for (let i = 0; i < keys.length; i++) totalLevelVol += this._profile[keys[i]]
    const avgLevelVol = totalLevelVol / keys.length
    const threshold   = avgLevelVol * this.hvnMultiplier

    const hvns = []
    for (let i = 0; i < keys.length; i++) {
      const vol = this._profile[keys[i]]
      if (vol >= threshold) {
        hvns.push({ mcap: Number(keys[i]), vol })
      }
    }

    if (hvns.length === 0) return

    hvns.sort((a, b) => b.vol - a.vol)
    const topN    = Math.min(3, hvns.length)
    // Percent floor OR ATR multiple, whichever is wider — on expansion a
    // fixed 3% window can be under one bar's range and starves HVN births.
    const maxDist = Math.max(this._lastClose * this.hvnProximityPercent, this.atr * this.hvnProximityAtrMult)

    for (let i = 0; i < topN; i++) {
      if (this._lastClose > 0 && Math.abs(hvns[i].mcap - this._lastClose) > maxDist) continue
      this._birthOrMerge(hvns[i].mcap, hvns[i].vol, time, 'hvn')
    }
  }

  // ─── Single candle POC ──────────────────────────────────────

  _getCandlePOC(candle) {
    if (!candle.isHistorical) {
      let maxVol = 0, pocMcap = null
      forEachLevel(candle, (mcap, buy, sell) => {
        const v = (buy || 0) + (sell || 0)
        if (v > maxVol) { maxVol = v; pocMcap = mcap }
      })
      if (pocMcap != null) return pocMcap
      // Fall through to HLC3 if the live candle somehow yielded no levels.
    }
    if (candle.high_mcap != null && candle.low_mcap != null && candle.close_mcap != null) {
      return (candle.high_mcap + candle.low_mcap + candle.close_mcap) / 3
    }
    return null
  }

  // ─── Candle field accessors ─────────────────────────────────

  _getCandleVolume(candle) {
    if (candle.isHistorical) return candle.volume_tokens ?? 0
    let total = 0
    forEachLevel(candle, (_mcap, buy, sell) => { total += (buy || 0) + (sell || 0) })
    return total
  }

  _getHigh(candle) {
    if (candle.raw_high_mcap != null) return candle.raw_high_mcap
    // Live candles carry raw high_mcap directly (the chart reads it the same
    // way); fall back to a level scan only very early in a candle's life
    // before that field is populated. Legacy mcapLevels is gone — forEachLevel
    // reads the real _liveLevels/_native data.
    if (candle.high_mcap != null && Number.isFinite(candle.high_mcap)) return candle.high_mcap
    let hi = -Infinity
    forEachLevel(candle, (mcap, buy, sell) => { if ((buy + sell) > 0 && mcap > hi) hi = mcap })
    return hi === -Infinity ? 0 : hi
  }

  _getLow(candle) {
    if (candle.raw_low_mcap != null) return candle.raw_low_mcap
    if (candle.low_mcap != null && Number.isFinite(candle.low_mcap)) return candle.low_mcap
    let lo = Infinity
    forEachLevel(candle, (mcap, buy, sell) => { if ((buy + sell) > 0 && mcap < lo) lo = mcap })
    return lo === Infinity ? 0 : lo
  }

  _getClose(candle) {
    if (candle.raw_close_mcap != null) return candle.raw_close_mcap
    if (candle.close_mcap != null) return candle.close_mcap
    return this._getHigh(candle)
  }

  // ─── Adaptive radius ────────────────────────────────────────
  // ONE length scale for birth, merge, touch, drift-cap, and render.
  // ATR-based — view-independent on purpose: zooming/rebucketing must not
  // change the signal (zones feed ARD later, spec G2b), which disqualifies
  // bucket-anchored detection. Floored at touchRadiusPercent so a dead
  // token degrades to exactly the old fixed-% behavior.

  _radius(price) {
    return Math.max(Math.abs(price) * this.touchRadiusPercent, this.atr * this.atrMult)
  }

  _refreshZoneBands() {
    for (const zone of this.zones) {
      const r = this._radius(zone.level)
      zone.upper = zone.level + r
      zone.lower = zone.level - r
    }
  }

  // ─── Zone birth / merge ─────────────────────────────────────
  // Merging only reinforces birth volume — does NOT count as a touch.

  _birthOrMerge(mcap, vol, time, source) {
    const thresh = this._radius(mcap) * this.clusterRadiusMult

    for (const zone of this.zones) {
      if (Math.abs(zone.level - mcap) <= thresh) {
        if (vol > zone.birthVolume) zone.birthVolume = vol
        return
      }
    }

    const radius    = this._radius(mcap)
    const birthSide = mcap <= this._lastClose ? 'below' : 'above'

    this.zones.push({
      level:         mcap,
      upper:         mcap + radius,
      lower:         mcap - radius,
      touches:       1,
      birthVolume:   vol,
      lastVolume:    vol,
      volumeTrend:   'strong',
      type:          source,
      strength:      this._calcStrength(1, vol, vol, 'strong'),
      birthTime:     time,
      lastTouchTime: time,
      lastTouchBar:  this._barIndex,
      touchHistory:  [{ time, volume: vol, side: birthSide }],
      _sides:        new Set([birthSide]),
      _barsOutside:  this.cooldownCandles,   // FIX: immediately eligible for first touch
    })
  }

  // ─── Touch tracking (reversal only + cooldown) ──────────────

  _touchZone(zone, data) {
    const { volume: vol, time, close, level } = data
    const side = data.pivotType
      ? (data.pivotType === 'high' ? 'above' : 'below')
      : (close >= zone.level ? 'above' : 'below')

    const last = zone.touchHistory[zone.touchHistory.length - 1]
    if (last && last.time === time) return

    zone.touches++
    zone.lastVolume    = vol
    zone.lastTouchTime = time
    zone.lastTouchBar  = this._barIndex
    zone.touchHistory.push({ time, volume: vol, side })
    zone._sides.add(side)
    zone._barsOutside = 0

    // Drift level toward reaction point — capped at half a radius per touch.
    // touchLevel is a RAW low/high, so the uncapped 15% pull let one
    // mega-wick yank the level a large absolute distance during range
    // expansion — the band visibly slid exactly when it should hold still.
    if (level != null) {
      const r   = this._radius(zone.level)
      let shift = (level - zone.level) * 0.15
      const cap = r * 0.5
      if (shift >  cap) shift =  cap
      if (shift < -cap) shift = -cap
      zone.level += shift
      zone.upper  = zone.level + r
      zone.lower  = zone.level - r
    }

    // Flip detection
    if (zone._sides.has('above') && zone._sides.has('below')) zone.type = 'flip'

    zone.volumeTrend = this._calcVolumeTrend(vol)
    zone.strength    = this._calcStrength(zone.touches, zone.birthVolume, zone.lastVolume, zone.volumeTrend)
  }

  // FIX: accepts prevHigh/prevLow/prevClose — replaces the always-true tautology
  // (close < zone.level / close >= zone.level) with actual previous candle data,
  // mirroring Pine Script's bar[1] approach exactly
  _checkTouches(high, low, close, vol, time, prevHigh, prevLow, prevClose) {
    for (const zone of this.zones) {
      const barInside = low <= zone.upper && high >= zone.lower

      if (barInside) {
        if (zone._barsOutside >= this.cooldownCandles) {
          const closedAbove = close > zone.upper
          const closedBelow = close < zone.lower

          const approachFromBelow = prevLow  < zone.lower || prevClose < zone.lower
          const approachFromAbove = prevHigh > zone.upper || prevClose > zone.upper

          let reversed = false
          if      (approachFromBelow && !closedAbove) reversed = true
          else if (approachFromAbove && !closedBelow) reversed = true
          else if (!closedAbove && !closedBelow)      reversed = true

          if (reversed) {
            const touchLevel = low <= zone.upper && low >= zone.lower ? low : high
            this._touchZone(zone, { level: touchLevel, volume: vol, time, close, pivotType: null })
          }
        }

        zone._barsOutside = 0
      } else {
        zone._barsOutside++
      }
    }
  }

  // ─── Scoring ────────────────────────────────────────────────

  _calcVolumeTrend(touchVol) {
    if (this.avgVolume <= 0) return 'strong'
    const ratio = touchVol / this.avgVolume
    if (ratio < this.decayRatio) return 'decaying'
    if (ratio < 0.7) return 'weakening'
    return 'strong'
  }

  _calcStrength(touches, birthVol, _lastVol, trend) {
    const t = Math.min(touches, 10)
    const v = this.avgVolume > 0 ? (birthVol / this.avgVolume) : 1
    const m = trend === 'decaying' ? 0.5 : trend === 'weakening' ? 0.75 : 1
    return t * v * m
  }

  // ─── Prune: age + overflow ──────────────────────────────────

  // FIX: removed unused `time` parameter
  _pruneZones() {
    // Pass 1: remove aged-out zones
    for (let i = this.zones.length - 1; i >= 0; i--) {
      const age = this._barIndex - (this.zones[i].lastTouchBar || 0)
      if (age > this.maxAgeBars) {
        this.zones.splice(i, 1)
      }
    }

    // Pass 2: trim to maxZones by removing weakest
    if (this.zones.length > this.maxZones) {
      this.zones.sort((a, b) => b.strength - a.strength)
      this.zones = this.zones.slice(0, this.maxZones)
    }
  }

  // ─── Public API ─────────────────────────────────────────────

  getZones() {
    return this.zones
      .filter(z => z.touches >= this.minTouches)
      .sort((a, b) => b.strength - a.strength)
      .map(z => ({
        level:         z.level,
        upper:         z.upper,
        lower:         z.lower,
        touches:       z.touches,
        birthVolume:   z.birthVolume,
        lastVolume:    z.lastVolume,
        volumeTrend:   z.volumeTrend,
        type:          z.type,
        strength:      z.strength,
        birthTime:     z.birthTime,
        lastTouchTime: z.lastTouchTime,
        touchHistory:  [...z.touchHistory],
      }))
  }

  getDecayingZones() {
    return this.getZones().filter(z => z.volumeTrend === 'decaying')
  }

  getNearestZone(mcap) {
    const active = this.getZones()
    if (!active.length) return null
    let nearest = null, minDist = Infinity
    for (const z of active) {
      const d = Math.abs(mcap - z.level)
      if (d < minDist) { minDist = d; nearest = z }
    }
    const radius = this._radius(nearest.level)
    return { zone: nearest, distance: minDist, approaching: minDist < radius * 3, atZone: minDist < radius }
  }

  reset() {
    this.zones        = []
    this.avgVolume    = 0
    this._volRing     = []
    this._candleRing  = []
    this._profile     = {}
    this._ingestCount = 0
    this._barIndex    = 0
    this._lastClose   = 0
    this._trRing      = []
    this.atr          = 0
    // FIX: reset prev candle state alongside everything else
    this._prevHigh    = 0
    this._prevLow     = 0
    this._prevClose   = 0
  }
}

// ─── Canvas Renderer ──────────────────────────────────────────

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {Array}    zones       - from detector.getZones()
 * @param {Function} mcapToY    - convert mcap → canvas Y
 * @param {number}   plotWidth  - chart width (excl RIGHT_PADDING)
 * @param {number}   totalHeight - total canvas height
 * @param {Function} [timeToX]  - optional: convert birthTime → canvas X. When
 *                                provided, lines and bands start at the bar
 *                                that birthed the zone (Pine-style). Without
 *                                it, lines/bands span the full chart width.
 */
export function drawFlipZones(ctx, zones, mcapToY, plotWidth, totalHeight, bucketSize, timeToX = null) {
  if (!zones.length) return
  ctx.save()

  for (const zone of zones) {
    const y      = mcapToY(zone.level)
    // Band = the zone's ACTUAL detection band. upper/lower carry the
    // detector's ATR-scaled radius, refreshed every bar — so drawn band ≡
    // touch band at every regime. The old synthetic 1.5-bucket band
    // overstated the touchable region by up to ~10× on wide-range charts
    // (bucket coarsens with range; the % detection radius didn't), so
    // price visibly entered painted zones that could never register a
    // touch. bucketSize now only supplies a MINIMUM drawn height (one
    // row) so a tight zone on a coarse grid never collapses to a hairline.
    let yUpper = mcapToY(zone.upper)
    let yLower = mcapToY(zone.lower)
    if (y == null || y < -50 || y > totalHeight + 50) continue
    const minBandPx = (bucketSize > 0) ? Math.abs(mcapToY(zone.level + bucketSize) - y) : 0
    if (Math.abs(yLower - yUpper) < minBandPx) {
      yUpper = y - minBandPx / 2
      yLower = y + minBandPx / 2
    }

    // Determine where the line/band starts.
    // If timeToX is provided AND yields a valid X for this zone's birthTime,
    // start at that X (clamped to 0 if zone was born before visible viewport).
    // Otherwise full-width fallback.
    let startX = 0
    if (timeToX && zone.birthTime) {
      const bx = timeToX(zone.birthTime)
      if (bx !== null && bx !== undefined && Number.isFinite(bx)) {
        startX = Math.max(0, Math.min(plotWidth, bx))
      }
    }
    const segWidth = Math.max(0, plotWidth - startX)
    if (segWidth <= 0) continue

    const isFlip  = zone.type === 'flip'
    const isDecay = zone.volumeTrend === 'decaying'
    const isWeak  = zone.volumeTrend === 'weakening'

    const lineColor = isFlip  ? '#00BFFF'
                    : isDecay ? '#FF4444'
                    : isWeak  ? '#FFA500'
                    : '#FFD700'
    const bandAlpha = isDecay ? 0.04 : isWeak ? 0.06 : 0.09

    // Band fill
    const bandH = Math.abs(yLower - yUpper)
    if (bandH > 0) {
      ctx.fillStyle   = lineColor
      ctx.globalAlpha = bandAlpha
      ctx.fillRect(startX, Math.min(yUpper, yLower), segWidth, bandH)
    }

    // Dashed center line — pixel-snapped (round + 0.5) like the grid
    // canvas, so the 1px dash stays crisp at fractional band centers.
    const ySnap = Math.round(y) + 0.5
    ctx.beginPath()
    ctx.setLineDash([6, 4])
    ctx.strokeStyle = lineColor
    ctx.lineWidth   = 1
    ctx.globalAlpha = isDecay ? 0.45 : 0.7
    ctx.moveTo(startX, ySnap)
    ctx.lineTo(plotWidth, ySnap)
    ctx.stroke()
    ctx.setLineDash([])
    ctx.globalAlpha = 1

    // Label
    const icon  = isDecay ? ' ⚠' : isWeak ? ' ↓' : ''
    const tag   = zone.type === 'flip'       ? 'FLIP'
                : zone.type === 'hvn'        ? 'HVN'
                : zone.type === 'poc'        ? 'POC'
                : zone.type === 'resistance' ? 'R' : 'S'
    const label = `${tag} ×${zone.touches}${icon}`

    const fontSize = 10
    ctx.font          = `bold ${fontSize}px "JetBrains Mono", monospace`
    const tw          = ctx.measureText(label).width
    const lx          = plotWidth - tw - 8
    const ly          = y + fontSize / 2

    ctx.globalAlpha   = 0.9
    ctx.shadowColor   = 'rgba(0,0,0,0.7)'
    ctx.shadowBlur    = 3
    ctx.fillStyle     = lineColor
    ctx.textBaseline  = 'alphabetic'
    ctx.fillText(label, lx, ly)
    ctx.shadowColor   = 'transparent'
    ctx.shadowBlur    = 0
  }

  ctx.restore()
}